import { useState } from "react";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import EntityDialog from "@/components/EntityDialog";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Package, Plus, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useOptions } from "@/lib/useOptions";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Inventory() {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const cats = useOptions("/categories");
  const brands = useOptions("/brands");
  const units = useOptions("/units", "code");

  const itemFields = [
    { name: "sku", label: "SKU", required: true },
    { name: "name", label: "Item Name", required: true },
    { name: "category", label: "Category", type: "select", options: cats.map((c) => ({ value: c.label, label: c.label })) },
    { name: "brand", label: "Brand", type: "select", options: brands.map((b) => ({ value: b.label, label: b.label })) },
    { name: "unit", label: "Unit", type: "select", options: units.map((u) => ({ value: u.label, label: u.label })) },
    { name: "barcode", label: "Barcode" },
    { name: "cost_price", label: "Cost Price", type: "number" },
    { name: "sale_price", label: "Sale Price", type: "number" },
    { name: "reorder_level", label: "Reorder Level", type: "number" },
    { name: "min_stock", label: "Min Stock", type: "number" },
    { name: "max_stock", label: "Max Stock", type: "number" },
    { name: "safety_stock", label: "Safety Stock", type: "number" },
    { name: "description", label: "Description", type: "textarea", full: true },
  ];

  const del = async (row, reload) => {
    if (!window.confirm(`Delete ${row.name}?`)) return;
    try { await api.delete(`/items/${row.id}`); toast.success("Deleted"); reload(); } catch { toast.error("Failed"); }
  };

  const cols = [
    { key: "sku", label: "SKU", mono: true },
    { key: "name", label: "Item", render: (r) => <span className="font-medium">{r.name}</span> },
    { key: "category", label: "Category" },
    { key: "brand", label: "Brand" },
    { key: "stock_qty", label: "Stock", render: (r) => <span className="font-mono">{r.stock_qty ?? 0}</span> },
    { key: "reorder_level", label: "Reorder" },
    { key: "low", label: "Status", render: (r) => <StatusBadge status={r.low ? "low" : "ok"} /> },
    { key: "cost_price", label: "Cost", render: (r) => `₹${r.cost_price ?? 0}` },
  ];

  const masterCols = [
    { key: "code", label: "Code", mono: true },
    { key: "name", label: "Name", render: (r) => <span className="font-medium">{r.name}</span> },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <div>
      <PageHeader title="Inventory" subtitle="Item master, categories & brands" icon={Package}
        breadcrumb={["Home", "Inventory"]}
        actions={can("inventory.create") && (
          <Button data-testid="add-item-btn" onClick={() => { setEditing(null); setOpen(true); }} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-1.5" />Add Item
          </Button>
        )} />

      <Tabs defaultValue="items">
        <TabsList className="mb-4">
          <TabsTrigger value="items" data-testid="tab-items">Items</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">Categories</TabsTrigger>
          <TabsTrigger value="brands" data-testid="tab-brands">Brands</TabsTrigger>
          <TabsTrigger value="units" data-testid="tab-units">Units</TabsTrigger>
        </TabsList>

        <TabsContent value="items">
          <DataTable endpoint="/items" columns={cols} testId="items-table" refreshKey={refresh}
            actions={(row, reload) => (
              <div className="flex items-center justify-end gap-1">
                {can("inventory.update") && <button data-testid={`edit-item-${row.id}`} onClick={() => { setEditing(row); setOpen(true); }} className="p-2 rounded-md hover:bg-slate-100 dark:hover:bg-secondary text-slate-500"><Pencil className="h-4 w-4" /></button>}
                {can("inventory.delete") && <button data-testid={`delete-item-${row.id}`} onClick={() => del(row, reload)} className="p-2 rounded-md hover:bg-red-50 text-red-500"><Trash2 className="h-4 w-4" /></button>}
              </div>
            )} />
        </TabsContent>
        <TabsContent value="categories"><MasterTab endpoint="/categories" cols={masterCols} label="Category" can={can} /></TabsContent>
        <TabsContent value="brands"><MasterTab endpoint="/brands" cols={masterCols} label="Brand" can={can} /></TabsContent>
        <TabsContent value="units"><MasterTab endpoint="/units" cols={masterCols} label="Unit" can={can} /></TabsContent>
      </Tabs>

      <EntityDialog open={open} onOpenChange={setOpen} title={editing ? "Edit Item" : "New Item"}
        fields={itemFields} endpoint="/items" initial={editing} onSaved={() => setRefresh((r) => r + 1)} />
    </div>
  );
}

function MasterTab({ endpoint, cols, label, can }) {
  const [open, setOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  return (
    <div>
      {can(`${endpoint.slice(1) === "units" ? "inventory" : "inventory"}.create`) && (
        <div className="mb-3 flex justify-end">
          <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`add-${label.toLowerCase()}-btn`}>
            <Plus className="h-4 w-4 mr-1.5" />Add {label}
          </Button>
        </div>
      )}
      <DataTable endpoint={endpoint} columns={cols} testId={`${label.toLowerCase()}-table`} refreshKey={refresh} searchable={false} />
      <EntityDialog open={open} onOpenChange={setOpen} title={`New ${label}`}
        fields={[{ name: "name", label: "Name", required: true }, { name: "code", label: "Code" }]}
        endpoint={endpoint} onSaved={() => setRefresh((r) => r + 1)} />
    </div>
  );
}
